"use client";

import React, { useEffect, useRef, useState } from "react";
import { cellExpr, gridMetrics, gridX } from "@/components/hero/HeroGrid";
import CubeCluster from "@/components/hero/CubeCluster";
import StarMarker from "@/components/hero/StarMarker";
import DashedPanel from "@/components/hero/DashedPanel";
import FloatingLabel from "@/components/FloatingLabel";

/** A decoration to place somewhere in the field (same vocabulary as the hero). */
export type DecoItem =
  | { type: "cube"; variant: "a" | "b" | "c" | "d" }
  | { type: "star"; size: number }
  | { type: "panel"; label: string }
  | { type: "label"; text: string; position: "top" | "bottom" };

/** Default inventory for the homepage reel — routing/analytics/API vocabulary. */
export const REEL_INVENTORY: DecoItem[] = [
  { type: "label", text: "GEO", position: "top" },
  { type: "label", text: "UTM", position: "bottom" },
  { type: "label", text: "MCP", position: "top" },
  { type: "label", text: "A/B", position: "bottom" },
  { type: "label", text: "DNS", position: "top" },
  { type: "label", text: "SSL", position: "bottom" },
  { type: "label", text: "WEBHOOK", position: "top" },
  { type: "label", text: "30X", position: "bottom" },
  { type: "label", text: "UTM", position: "top" },
  { type: "label", text: ".JSON", position: "bottom" },
  { type: "label", text: "QR", position: "top" },
  { type: "label", text: "IF · ELSE", position: "bottom" },
  { type: "star", size: 36 },
  { type: "star", size: 32 },
  { type: "star", size: 34 },
  { type: "star", size: 30 },
  { type: "star", size: 33 },
  { type: "panel", label: "[1×1]" },
  { type: "panel", label: "[1×1]" },
  { type: "panel", label: "[1×1]" },
  { type: "cube", variant: "a" },
  { type: "cube", variant: "b" },
  { type: "cube", variant: "c" },
  { type: "cube", variant: "d" },
];

type Cell = { g: number; r: number; w: number };
type Placement = DecoItem & {
  id: number;
  gcol: number;
  row: number;
  key: number;
  delay: number;
  leaving?: boolean;
};

const TICK_MS = 3400;
const FADE_OUT_MS = 650;

/**
 * The hero's living decoration field, generalized for content sections: an
 * absolute layer (mount inside a `relative` container) that scatters
 * grid-cell-aligned glyphs into cells NOT occupied by content. Forbidden
 * cells come from every `[data-grid-block]` and `[data-deco-avoid]` rect
 * inside the layer, padded — so decorations always live in the margins.
 * Rows are aligned to the page grid even when the layer starts mid-cell.
 * Desktop-only; a rotation tick keeps the field slowly changing.
 */
export default function SectionDecorations({
  inventory = REEL_INVENTORY,
  density = 5,
}: {
  inventory?: DecoItem[];
  /** Rough number of visible items per viewport-height of the layer. */
  density?: number;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const poolRef = useRef<Cell[]>([]);
  const maxVisibleRef = useRef(0);
  const keyRef = useRef(1);
  const [rowOffset, setRowOffset] = useState(0);
  const [placements, setPlacements] = useState<Placement[]>([]);

  const computePool = (): { pool: Cell[]; offset: number; maxVisible: number } => {
    const root = rootRef.current;
    if (!root) return { pool: [], offset: 0, maxVisible: 0 };
    const w = document.documentElement.clientWidth;
    if (w < 768) return { pool: [], offset: 0, maxVisible: 0 };

    const { cell, offsetX } = gridMetrics(w);
    const layer = root.getBoundingClientRect();
    const layerTopDoc = layer.top + window.scrollY;
    // Align decoration rows to the page grid: first full row inside the layer.
    const offset = Math.ceil(layerTopDoc / cell) * cell - layerTopDoc;

    const forbidden = new Set<string>();
    const addRect = (rect: DOMRect, padX: number, padY: number) => {
      const g0 = Math.floor((rect.left - padX * cell - offsetX) / cell);
      const g1 = Math.ceil((rect.right + padX * cell - offsetX) / cell);
      const r0 = Math.floor((rect.top - layer.top - offset - padY * cell) / cell);
      const r1 = Math.ceil((rect.bottom - layer.top - offset + padY * cell) / cell);
      for (let r = r0; r < r1; r++) for (let g = g0; g < g1; g++) forbidden.add(`${g},${r}`);
    };
    root.parentElement
      ?.querySelectorAll<HTMLElement>("[data-grid-block], [data-deco-avoid]")
      .forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.width && r.height) addRect(r, 0.35, 0.35);
      });

    const gColMin = Math.ceil(-offsetX / cell);
    const gColMax = Math.floor((w - offsetX) / cell) - 1;
    const rows = Math.floor((layer.height - offset) / cell);
    const centerG = (gColMin + gColMax) / 2;
    const halfSpan = Math.max(1, (gColMax - gColMin) / 2);

    const pool: Cell[] = [];
    for (let r = 0; r < rows; r++) {
      for (let g = gColMin; g <= gColMax; g++) {
        if (forbidden.has(`${g},${r}`)) continue;
        const edge = Math.pow(Math.abs(g - centerG) / halfSpan, 1.6);
        pool.push({ g, r, w: 0.12 + edge });
      }
    }
    const viewportRows = Math.max(1, Math.floor(window.innerHeight / cell));
    const maxVisible = Math.min(
      inventory.length,
      Math.max(3, Math.round((rows / viewportRows) * density)),
    );
    return { pool, offset, maxVisible };
  };

  const pickOne = (avail: Cell[]): Cell => {
    const total = avail.reduce((s, c) => s + c.w, 0);
    let t = Math.random() * total;
    for (const c of avail) {
      t -= c.w;
      if (t <= 0) return c;
    }
    return avail[avail.length - 1];
  };

  const layout = (pool: Cell[], maxVisible: number): Placement[] => {
    const occupied = new Set<string>();
    const out: Placement[] = [];
    const ids = inventory
      .map((_, id) => id)
      .sort(() => Math.random() - 0.5)
      .slice(0, maxVisible);
    ids.forEach((id) => {
      const avail = pool.filter((c) => !occupied.has(`${c.g},${c.r}`));
      if (!avail.length) return;
      const pick = pickOne(avail);
      occupied.add(`${pick.g},${pick.r}`);
      out.push({
        ...inventory[id],
        id,
        gcol: pick.g,
        row: pick.r,
        key: keyRef.current++,
        delay: Math.random() * 2,
      });
    });
    return out;
  };

  const rebuild = () => {
    const { pool, offset, maxVisible } = computePool();
    poolRef.current = pool;
    maxVisibleRef.current = maxVisible;
    setRowOffset(offset);
    setPlacements(pool.length ? layout(pool, maxVisible) : []);
  };

  useEffect(() => {
    // Content blocks and row-snapping settle after mount — build once things
    // are laid out, then track fonts/resize/relayout.
    const t = setTimeout(rebuild, 100);
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(rebuild).catch(() => {});

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(rebuild);
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("grid-relayout", schedule);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("grid-relayout", schedule);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotation tick: retire one item, bring a hidden one in elsewhere.
  useEffect(() => {
    if (!placements.length) return;
    const id = setInterval(() => {
      setPlacements((prev) => {
        const retireable = prev.filter((p) => !p.leaving);
        if (retireable.length <= 2) return prev;
        const victim = retireable[Math.floor(Math.random() * retireable.length)];
        return prev.map((p) => (p === victim ? { ...p, leaving: true } : p));
      });
      setTimeout(() => {
        setPlacements((prev) => {
          const staying = prev.filter((p) => !p.leaving);
          const pool = poolRef.current;
          if (!pool.length || staying.length >= maxVisibleRef.current) return staying;
          const occupied = new Set(staying.map((p) => `${p.gcol},${p.row}`));
          const activeIds = new Set(staying.map((p) => p.id));
          const hidden = inventory
            .map((item, id) => ({ item, id }))
            .filter((x) => !activeIds.has(x.id));
          if (!hidden.length) return staying;
          const choice = hidden[Math.floor(Math.random() * hidden.length)];
          const avail = pool.filter((c) => !occupied.has(`${c.g},${c.r}`));
          if (!avail.length) return staying;
          const cell = pickOne(avail);
          return [
            ...staying,
            {
              ...choice.item,
              id: choice.id,
              gcol: cell.g,
              row: cell.r,
              key: keyRef.current++,
              delay: Math.random() * 1.2,
            },
          ];
        });
      }, FADE_OUT_MS);
    }, TICK_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements.length]);

  const renderInner = (p: Placement) => {
    switch (p.type) {
      case "cube":
        return <CubeCluster variant={p.variant} />;
      case "star":
        return <StarMarker size={p.size} delay={p.delay} />;
      case "panel":
        return <DashedPanel cellsW={1} cellsH={1} label={p.label} />;
      case "label":
        return <FloatingLabel text={p.text} position={p.position} />;
    }
  };

  return (
    <div
      ref={rootRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 hidden overflow-hidden md:block"
    >
      {placements.map((p) => (
        <span
          key={`${p.id}-${p.key}`}
          className={`absolute ${p.leaving ? "animate-deco-out" : "animate-deco-fade"}`}
          style={{
            left: gridX(p.gcol),
            top: `calc(${rowOffset}px + ${cellExpr({ cells: p.row })})`,
            animationDelay: p.leaving ? undefined : `${p.delay * 0.15}s`,
          }}
        >
          {renderInner(p)}
        </span>
      ))}
    </div>
  );
}
